import Dexie, { type EntityTable } from 'dexie';

/**
 * All four tables are defined now, though stage 1 only writes to three.
 * Migrating Dexie later is doable but tedious, and getting the image table
 * right up front is what makes stage 2's masonry work.
 *
 * Ids are permanent — they end up in backup files and the user's own exports.
 * Nothing regenerates them.
 */

export type Room = {
  id: string;
  name: string;
  /** A lucide component name, chosen by the user. See rooms/roomIcons.ts. */
  icon: string;
  // `| undefined` throughout: exactOptionalPropertyTypes is on, and a record
  // built with `coverImageId: undefined` is the normal shape here.
  coverImageId?: string | undefined;
  sort: number;
  createdAt: number;
};

/**
 * A group of things inside a room, made by dragging one item onto another.
 * Sections nest: a section's parent is another section, or the room itself.
 *
 * Deliberately not a room and not an item. It owns nothing — the items keep
 * their `roomId` whatever depth they sit at — so a section can be dissolved
 * without touching a single photo.
 */
export type Section = {
  id: string;
  roomId: string;
  /** The section this one sits in. Undefined at the room's top level. */
  parentId?: string | undefined;
  /** Empty until named. Shown as "Unnamed Section". */
  name: string;
  /** Optional. Without one, the four newest things inside make the cover. */
  coverImageId?: string | undefined;
  sort: number;
  createdAt: number;
};

export type Item = {
  id: string;
  roomId: string;
  /** The section it sits in. Undefined at the room's top level. */
  sectionId?: string | undefined;
  title: string;
  /** Crystallised from the store listing. A re-import overwrites this. */
  description: string;
  /** The user's. No automated process ever writes here. */
  notes: string;
  price?: number | undefined;
  currency?: string | undefined;
  sourceUrl?: string | undefined;
  coverImageId?: string | undefined;
  /** Gallery, ordered, excludes the cover. */
  imageIds: string[];
  /** When link import last ran. */
  capturedAt?: number | undefined;
  sort: number;
  createdAt: number;
};

export type StoredImage = {
  id: string;
  /** The original, downscaled to at most 2000px on its longest edge. */
  blob: Blob;
  /**
   * WebP, longest edge 480px. Boards and cover cards render this; only the
   * item panel loads `blob`. Not in the stage doc's schema, but CLAUDE.md's
   * "every image is stored twice" rule is what keeps a wall of tiles fast,
   * and it costs nothing to honour now versus a migration in stage 2.
   */
  thumb: Blob;
  /** Intrinsic pixel width of `blob`. REQUIRED — see the note below. */
  w: number;
  /** Intrinsic pixel height of `blob`. REQUIRED. */
  h: number;
  mime: string;
  addedAt: number;
};

/**
 * Where an item's text came from, and when. Kept so that months later you can
 * tell a description the store wrote from one you typed yourself, and so a
 * re-import knows which URL to go back to.
 *
 * One row per import, not one per item: re-importing appends.
 */
export type ImportMethod =
  /** An adapter that knows this shop's markup. See `import/sites/`. */
  | 'site'
  /** schema.org Product in `application/ld+json`. The good case. */
  | 'jsonld'
  /** OpenGraph meta tags. */
  | 'og'
  /** `<title>` and the largest image on the page. Last resort. */
  | 'html';

export type ImportRecord = {
  id: string;
  itemId: string;
  /** What was pasted. */
  url: string;
  /** After redirects — retailers love tracking-parameter hops. */
  finalUrl: string;
  fetchedAt: number;
  method: ImportMethod;
};

export type Setting = {
  key: string;
  value: unknown;
};

/**
 * `w` and `h` are not optional. The masonry in stage 2 must know each photo's
 * aspect ratio *before* the image decodes, or the grid reflows chaotically as
 * pictures load in. They are measured at upload time and stored.
 */
export class VisiListDb extends Dexie {
  rooms!: EntityTable<Room, 'id'>;
  items!: EntityTable<Item, 'id'>;
  sections!: EntityTable<Section, 'id'>;
  images!: EntityTable<StoredImage, 'id'>;
  imports!: EntityTable<ImportRecord, 'id'>;
  settings!: EntityTable<Setting, 'key'>;

  constructor() {
    super('visilist');
    this.version(1).stores({
      rooms: 'id, sort, createdAt',
      items: 'id, roomId, sort, createdAt',
      images: 'id',
      settings: 'key',
    });

    // Sections. No upgrade function: an item with no `sectionId` is an item
    // at the top level of its room, which is exactly what every existing one
    // is. Dexie rebuilds the item indexes on its own.
    this.version(2).stores({
      rooms: 'id, sort, createdAt',
      items: 'id, roomId, sectionId, sort, createdAt',
      sections: 'id, roomId, parentId, sort',
      images: 'id',
      settings: 'key',
    });

    // Link import. No upgrade function: every existing item was made by hand
    // and correctly has no import history at all.
    this.version(3).stores({
      rooms: 'id, sort, createdAt',
      items: 'id, roomId, sectionId, sort, createdAt',
      sections: 'id, roomId, parentId, sort',
      images: 'id',
      imports: 'id, itemId, fetchedAt',
      settings: 'key',
    });
  }
}

export const db = new VisiListDb();

export function newId(): string {
  return crypto.randomUUID();
}
